import React from 'react';
import SEO from '../components/layout/SEO';
import { FileText, Code, Download, BookOpen, Video, Users, Settings, BarChart3, Shield, Zap } from 'lucide-react';

const Docs: React.FC = () => {
  const sections = [
    {
      icon: BookOpen,
      title: 'Getting Started',
      description: 'Quick start guides and tutorials',
      articles: [
        'Installation Guide',
        'First Steps',
        'Basic Configuration',
        'User Onboarding'
      ]
    },
    {
      icon: Users,
      title: 'User Management',
      description: 'Managing users and permissions',
      articles: [
        'Creating User Accounts',
        'Role-Based Access Control',
        'User Groups',
        'Authentication Methods'
      ]
    },
    {
      icon: BarChart3,
      title: 'API Reference',
      description: 'Complete API documentation',
      articles: [
        'Authentication',
        'User Management API',
        'Course Management API',
        'Analytics API'
      ]
    },
    {
      icon: Settings,
      title: 'Configuration',
      description: 'System configuration and customization',
      articles: [
        'Environment Variables',
        'Database Configuration',
        'Email Settings',
        'Security Settings'
      ]
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Security best practices and guidelines',
      articles: [
        'Data Encryption',
        'Access Control',
        'Audit Logging',
        'Compliance Guidelines'
      ]
    },
    {
      icon: Zap,
      title: 'Performance',
      description: 'Optimization and performance tuning',
      articles: [
        'Caching Strategies',
        'Database Optimization',
        'CDN Configuration',
        'Monitoring Setup'
      ]
    }
  ];

  const codeExamples = [
    {
      title: 'Authentication Example',
      language: 'javascript',
      code: `// Initialize the tadriso LMS client
const tadriso = new tadrisoClient({
  apiKey: 'your-api-key',
  baseUrl: 'https://api.tadriso.ciphermind.tech'
});

// Authenticate a user
const user = await tadriso.auth.login({
  email: 'user@example.com',
  password: 'password'
});`
    },
    {
      title: 'Create Course Example',
      language: 'javascript',
      code: `// Create a new course
const course = await tadriso.courses.create({
  title: 'Introduction to Programming',
  description: 'Learn the basics of programming',
  instructorId: 'instructor-123',
  startDate: '2024-02-01',
  endDate: '2024-05-01'
});

// Add students to the course
await tadriso.courses.addStudents(course.id, [
  'student-1',
  'student-2',
  'student-3'
]);`
    },
    {
      title: 'Analytics Query Example',
      language: 'javascript',
      code: `// Get student progress analytics
const analytics = await tadriso.analytics.getStudentProgress({
  courseId: 'course-123',
  dateRange: {
    start: '2024-01-01',
    end: '2024-01-31'
  },
  metrics: ['completion_rate', 'engagement_score', 'assessment_scores']
});

console.log('Student Progress:', analytics);`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Documentation - tadriso LMS"
        description="Complete documentation for tadriso LMS. Find guides, API references, and technical documentation."
        keywords="documentation, API reference, technical docs, tadriso LMS, developer guide"
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Documentation
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Complete guides, API references, and technical documentation for tadriso LMS.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200">
                <Download size={20} className="inline mr-2" />
                Download PDF
              </button>
              <button className="px-6 py-3 border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold rounded-lg transition-colors duration-200">
                <Video size={20} className="inline mr-2" />
                Watch Tutorials
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Documentation Sections
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <section.icon size={24} className="text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {section.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {section.description}
                </p>
                <ul className="space-y-2">
                  {section.articles.map((article, articleIndex) => (
                    <li key={articleIndex} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                      {article}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Code Examples
          </h2>
          <div className="space-y-8">
            {codeExamples.map((example, index) => (
              <div key={index} className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {example.title}
                </h3>
                <div className="bg-gray-800 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-400 text-sm">
                    <code>{example.code}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Additional Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Code size={24} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">SDK Downloads</h3>
                  <p className="text-gray-600 text-sm">Official SDKs and libraries</p>
                </div>
              </div>
              <div className="space-y-2">
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  JavaScript SDK
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  Python SDK
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  PHP SDK
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  .NET SDK
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Video size={24} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Video Tutorials</h3>
                  <p className="text-gray-600 text-sm">Step-by-step video guides</p>
                </div>
              </div>
              <div className="space-y-2">
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  Getting Started Series
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  API Integration Tutorials
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  Advanced Features
                </a>
                <a href="#" className="block text-primary-600 hover:text-primary-700 text-sm">
                  Best Practices
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Need Technical Support?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Our technical team is here to help with implementation and integration questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:dev@ciphermind.tech"
              className="px-6 py-3 bg-white text-primary-600 hover:bg-gray-100 font-semibold rounded-lg transition-colors duration-200"
            >
              Contact Dev Team
            </a>
            <a
              href="/help"
              className="px-6 py-3 border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold rounded-lg transition-colors duration-200"
            >
              Visit Help Center
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Docs; 